import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Chip, Divider, Typography, alpha, useTheme } from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import Image from '../../components/Image';
import { PayNow } from '@Components/PayNow';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '../../store';
import { getShipmentBySN, getShipmentPaymentBySN } from '@Reducers/Shipment/actions';
import { getShipmentWebsocketStream } from '@Reducers/AdminShipment/actions';
import { PricingDataType } from '../../store/reducers/Shipment/Type';
import { formatNumber, useLocale } from '../../utils/formatNumber';

function ShipmentCharges() {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const { enqueueSnackbar } = useSnackbar();

  const { shipmentById, shipmentPayment } = useSelector((state: RootStateType) => state.shipment);
  const { sellingPricing } = shipmentById || {};
  const { payableAmount = 0, totalPaidAmt, tdsAmount = 0, _transactionsId } = shipmentPayment || {};
  const successfulTransactions = _transactionsId?.filter(payment => payment.status === 'successful') || [];
  const totalAmount = successfulTransactions.reduce((sum, payment) => sum + payment.amount, 0);
  const totalTdsAmount = successfulTransactions.reduce((sum, payment) => sum + payment.tdsAmount, 0);
  const shipmentNumber = shipmentById?.shipmentNumber || '';
  const locale = useLocale();

  const pricing = sellingPricing?.pricing;
  const taxPricing = sellingPricing?.taxPricing;
  const totalPricing = sellingPricing?.totalPricing;

  const [isWyngsMy, setIsWyngsMy] = useState<boolean>(false);

  useEffect(() => {
    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0];
    if (subdomain === 'wyngs-my') {
      setIsWyngsMy(true);
    } else {
      setIsWyngsMy(false);
    }
  }, []);

  const handlePaymentSuccess = () => {
    if (shipmentNumber) {
      dispatch(getShipmentPaymentBySN(shipmentNumber));
      dispatch(getShipmentBySN(shipmentNumber));
      dispatch(getShipmentWebsocketStream(['shipment-update-' + shipmentNumber]));
    }
    enqueueSnackbar('Payment done successfully!', { variant: 'success', key: new Date().getTime() });
  };

  const afterTaxPay = payableAmount - tdsAmount;
  const orgCountryCurrency = useSelector(
    (state: RootStateType) => state.orgSetup.organizationData?.currency?.name || '',
  );
  const currencyDisplay = isWyngsMy ? 'MYR' : sellingPricing?.currency || orgCountryCurrency;

  return (
    <Card
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: '100%',
        borderRadius: '14px',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
        bgcolor: theme.palette.background.paper,
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Card Header */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ReceiptLongOutlinedIcon />
        </Box>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>Shipment Charges</Typography>
            <Chip
              label="Charges"
              size="small"
              sx={{
                height: 20,
                fontSize: '0.6875rem',
                fontWeight: 600,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
              }}
            />
          </Box>
          <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>
            Pricing breakdown & billing settlement
          </Typography>
        </Box>
      </Box>

      {/* Card Content */}
      <CardContent sx={{ p: { xs: 2, sm: 3 }, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Charges Sub-section */}
        <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main', mb: 1 }}>Charges</Typography>

        <Box sx={{ width: '100%' }}>
          {pricing?.length
            ? pricing.map((price: PricingDataType, ind: number) => (
                <Box
                  key={`charges-breakup-${ind}`}
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}
                >
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black' }}>
                    {price.displayKey}
                  </Typography>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black' }}>
                    {formatNumber(price.value, locale)}
                  </Typography>
                </Box>
              ))
            : null}

          {taxPricing?.length
            ? taxPricing.map((price: PricingDataType, ind: number) => (
                <Box
                  key={`tax-charges-breakup-${ind}`}
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}
                >
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black' }}>
                    {price.displayKey}
                  </Typography>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black' }}>
                    {formatNumber(price.value, locale)}
                  </Typography>
                </Box>
              ))
            : null}

          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'common.black' }}>Total</Typography>
            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'primary.main' }}>
              {currencyDisplay} {totalPricing ? formatNumber(totalPricing, locale) : '0.00'}
            </Typography>
          </Box>
        </Box>

        {/* Payment Sub-section */}
        <Divider sx={{ my: 2 }} />

        <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main', mb: 1 }}>Payment</Typography>

        <Box sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black' }}>Amount Paid</Typography>
              {payableAmount - tdsAmount === 0 && (
                <Image src="/images/paid.svg" alt="paid-icon" height={12} style={{ marginLeft: 5, marginTop: 2 }} />
              )}
            </Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black' }}>
              {currencyDisplay}{' '}
              {tdsAmount == 0 && totalTdsAmount == 0
                ? totalPaidAmt && formatNumber(totalPaidAmt, locale)
                : formatNumber(totalAmount, locale)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black' }}>
              {payableAmount < 0 ? 'Refund Amount' : 'Amount Due'}
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black' }}>
              {currencyDisplay}{' '}
              {payableAmount == 0 ? payableAmount : formatNumber(Number(payableAmount - tdsAmount), locale)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black' }}>Deductions</Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black' }}>
              {currencyDisplay} {formatNumber(totalTdsAmount + tdsAmount, locale)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <PayNow paymentDetails={{ shipmentNumber, payableAmount: afterTaxPay, handlePaymentSuccess }} />
        </Box>
      </CardContent>
    </Card>
  );
}

export default ShipmentCharges;
